import React, { useEffect, useState, useRef } from "react";
import { Container, Button, Alert, ProgressBar, Card, Spinner, Overlay } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaExternalLinkAlt, FaArchive, FaDatabase, FaFileDownload } from "react-icons/fa";
import { GroupReportBar } from "@/components/GroupReportBar";
import {
  ArchiveRunSummaryDto,
  getSiteStorageDetailByGuid,
  getAdminArchiveRunsBySite,
  queueAdminArchiveCleanup,
  queueAdminArchiveReadonly,
  startAdminArchive,
  SiteStorageDetailDto,
  SiteStorageSnapshotDto,
} from "@/api/sharepointApi";
import { getReportRunsBySiteUrl, getGeneratedReports, ReportRun, GeneratedReport } from "@/api/adminApi";
import { getGroupMembersByGraphId } from "@/services/resourcesApi";
import { ResourceMember } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import IncreaseStorageModal from "@/components/IncreaseStorageModal";
import ResourceFileInventory from "@/pages/Resources/ResourceFileInventory";
import "./AdminGroupDetail.scss";

function formatGb(mb: number): string {
  return (mb / 1024).toFixed(1) + " GB";
}

function progressVariant(pct: number): string {
  if (pct > 85) return "danger";
  if (pct > 70) return "warning";
  return "success";
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

interface AdminGroupDetailProps {
  siteGuid?: string;
  embedded?: boolean;  // if true, render as tab pane without container/back link
}

const AdminGroupDetail: React.FC<AdminGroupDetailProps> = ({ siteGuid: propSiteGuid, embedded = false }) => {
  const { siteGuid: routeSiteGuid } = useParams<{ siteGuid: string }>();
  const effectiveSiteGuid = propSiteGuid || routeSiteGuid;

  const [detail, setDetail] = useState<SiteStorageDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showIncreaseModal, setShowIncreaseModal] = useState(false);
  const [detailTabId, setDetailTabId] = useState<"details" | "reports" | "users" | "files" | "archive">("details");

  // Reports tab state
  const [reports, setReports] = useState<ReportRun[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsLoaded, setReportsLoaded] = useState(false);

  // Users tab state
  const [members, setMembers] = useState<ResourceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [membersLoaded, setMembersLoaded] = useState(false);

  // Generated reports modal state
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
  const [generatedReportsLoading, setGeneratedReportsLoading] = useState(false);
  const [expandedReportRunId, setExpandedReportRunId] = useState<number | null>(null);
  const reportButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const [archiveRuns, setArchiveRuns] = useState<ArchiveRunSummaryDto[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiveActionLoading, setArchiveActionLoading] = useState(false);

  usePageTitle(detail?.site?.title, "Group Storage Detail");

  useEffect(() => {
    if (!effectiveSiteGuid?.trim()) { setError("Invalid site GUID."); setLoading(false); return; }
    const load = async () => {
      try {
        const data = await getSiteStorageDetailByGuid(effectiveSiteGuid);
        setDetail(data);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404) setError("Site not found.");
        else setError(err?.response?.data?.error ?? err?.message ?? "Failed to load site details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [effectiveSiteGuid]);

  // Load reports when Reports tab is selected (only once)
  useEffect(() => {
    if (detailTabId !== "reports" || reportsLoaded || !detail?.site?.url) return;

    const loadReports = async () => {
      try {
        setReportsLoading(true);
        setReportsError(null);
        const runs = await getReportRunsBySiteUrl(detail.site.url);
        setReports(runs);
        setReportsLoaded(true);
      } catch (err: any) {
        setReportsError(err?.response?.data?.error ?? err?.message ?? "Failed to load reports");
      } finally {
        setReportsLoading(false);
      }
    };

    loadReports();
  }, [detailTabId, reportsLoaded, detail?.site?.url]);

  // Load group members when Users tab is selected (only once)
  useEffect(() => {
    if (detailTabId !== "users" || !detail?.site?.groupId || membersLoaded) return;

    const loadMembers = async () => {
      try {
        setMembersLoading(true);
        setMembersError(null);
        const membersList = await getGroupMembersByGraphId(detail.site.groupId!);
        setMembers(membersList);
        setMembersLoaded(true);
      } catch (err: any) {
        setMembersError(err?.response?.data?.error ?? err?.message ?? "Failed to load members");
      } finally {
        setMembersLoading(false);
      }
    };

    loadMembers();
  }, [detailTabId, detail?.site?.groupId, membersLoaded]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (expandedReportRunId && !target.closest(".report-action-btn") && !target.closest(".report-tooltip")) {
        setExpandedReportRunId(null);
      }
    };

    if (expandedReportRunId) {
      document.addEventListener("click", handleDocumentClick);
      return () => document.removeEventListener("click", handleDocumentClick);
    }
  }, [expandedReportRunId]);

  const loadArchiveRuns = async () => {
    if (!effectiveSiteGuid) return;
    try {
      setArchiveLoading(true);
      setArchiveError(null);
      const runs = await getAdminArchiveRunsBySite(effectiveSiteGuid);
      setArchiveRuns(runs);
    } catch (err: any) {
      setArchiveError(err?.response?.data?.error ?? err?.message ?? "Failed to load archive runs.");
    } finally {
      setArchiveLoading(false);
    }
  };

  useEffect(() => {
    if (detailTabId === "archive") {
      loadArchiveRuns();
    }
  }, [detailTabId, effectiveSiteGuid]);

  const handleArchiveAction = async (action: "backup" | "readonly" | "cleanup") => {
    if (!effectiveSiteGuid) return;
    const latestGuid = archiveRuns[0]?.guid;

    try {
      setArchiveActionLoading(true);
      setArchiveError(null);

      if (action === "backup") {
        await startAdminArchive(effectiveSiteGuid, latestGuid);
      } else if (action === "readonly") {
        await queueAdminArchiveReadonly(effectiveSiteGuid, latestGuid);
      } else {
        await queueAdminArchiveCleanup(effectiveSiteGuid, latestGuid);
      }

      await loadArchiveRuns();
    } catch (err: any) {
      setArchiveError(err?.response?.data?.error ?? err?.message ?? `Failed to queue archive ${action}.`);
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const handleViewGeneratedReports = async (m365ReportRunId: number) => {
    // If already expanded, collapse it
    if (expandedReportRunId === m365ReportRunId) {
      setExpandedReportRunId(null);
      return;
    }

    try {
      setGeneratedReportsLoading(true);
      const reports = await getGeneratedReports(m365ReportRunId, detail?.site?.url);
      setGeneratedReports(reports);
      
      // If only one report, open it directly in new tab
      if (reports.length === 1) {
        window.open(reports[0].reportLink, "_blank");
      } else {
        // Multiple reports or none - show popover
        setExpandedReportRunId(m365ReportRunId);
      }
    } catch (err: any) {
      alert("Failed to fetch generated reports: " + (err?.message || "Unknown error"));
    } finally {
      setGeneratedReportsLoading(false);
    }
  };

  const site = detail?.site;
  const history = detail?.history ?? [];

  const content = (
    <>
      {!embedded && (
        <div className="mb-3">
          <Link to="/admin/groups" className="text-decoration-none text-muted small">
            <FaArrowLeft className="me-1" />
            Groups Admin
          </Link>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && <div className="text-muted">Loading…</div>}

      {!loading && site && (
        <>
          <GroupReportBar
            context={{
              siteUrl: site.url,
              groupId: site.groupId ?? undefined,
              displayName: site.title,
            }}
            compact={false}
          />

          <div className="my-4">
            <h3 className="mb-1">{site.title}</h3>
            <p className="text-muted small mb-3">{site.url}</p>

            <div className="d-flex gap-2 mb-4">
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <FaExternalLinkAlt className="me-1" />
                Open SharePoint
              </a>
              <Button
                size="sm"
                variant="outline-warning"
                onClick={() => setShowIncreaseModal(true)}
              >
                <FaDatabase className="me-1" />
                Increase Storage
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => setDetailTabId("archive")}
              >
                <FaArchive className="me-1" />
                Archive
              </Button>
            </div>

            {/* Sub-tabs for Details, Reports, Users */}
            <div className="admin-group-detail-tabs">
              <button
                className={`detail-tab-btn ${detailTabId === "details" ? "active" : ""}`}
                onClick={() => setDetailTabId("details")}
              >
                Details
              </button>
              <button
                className={`detail-tab-btn ${detailTabId === "reports" ? "active" : ""}`}
                onClick={() => setDetailTabId("reports")}
              >
                Reports
              </button>
              <button
                className={`detail-tab-btn ${detailTabId === "users" ? "active" : ""}`}
                onClick={() => setDetailTabId("users")}
              >
                Users
              </button>
              <button
                className={`detail-tab-btn ${detailTabId === "files" ? "active" : ""}`}
                onClick={() => setDetailTabId("files")}
              >
                Files
              </button>
              <button
                className={`detail-tab-btn ${detailTabId === "archive" ? "active" : ""}`}
                onClick={() => setDetailTabId("archive")}
              >
                Archive
              </button>
            </div>
          </div>

          {/* DETAILS TAB */}
          {detailTabId === "details" && (
            <>
              {/* Current Storage */}
              <Card className="mb-4">
                <Card.Header className="fw-semibold">Current Storage</Card.Header>
                <Card.Body>
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <small className="d-block text-muted mb-1">Used</small>
                      <strong className="d-block">{formatGb(site.storageUsedMb)}</strong>
                    </div>
                    <div className="col-sm-6">
                      <small className="d-block text-muted mb-1">Quota</small>
                      <strong className="d-block">{formatGb(site.storageQuotaMb)}</strong>
                    </div>
                    <div className="col-12">
                      <small className="d-block text-muted mb-1">Usage</small>
                      <ProgressBar
                    now={Math.min(site.storageUsagePercent, 100)}
                    variant={progressVariant(site.storageUsagePercent)}
                    label={`${site.storageUsagePercent.toFixed(1)}%`}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Metadata */}
          <Card className="mb-4">
            <Card.Header className="fw-semibold">Details</Card.Header>
            <Card.Body>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: 180 }}>Site ID</td>
                    <td className="font-monospace small">{site.siteCollectionId}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Site GUID</td>
                    <td className="font-monospace small">{site.guid}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Template</td>
                    <td>{site.template ?? "—"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Last Snapshot</td>
                    <td>{formatDateTime(site.lastSnapshotDate)}</td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>

          {/* Storage History */}
          {history && history.length > 0 && (
            <Card>
              <Card.Header className="fw-semibold">Storage History</Card.Header>
              <Card.Body>
                {history.length === 0 ? (
                  <p className="text-muted mb-0">No history available.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Snapshot Date</th>
                          <th>Quota</th>
                          <th>Used</th>
                          <th>Usage %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((snap: SiteStorageSnapshotDto, idx: number) => (
                          <tr key={idx}>
                            <td className="text-nowrap">{formatDateTime(snap.snapshotDate)}</td>
                            <td className="text-nowrap">{formatGb(snap.storageQuotaMb)}</td>
                            <td className="text-nowrap">{formatGb(snap.storageUsedMb)}</td>
                            <td>
                              <ProgressBar
                                now={Math.min(snap.storageUsagePercent, 100)}
                                variant={progressVariant(snap.storageUsagePercent)}
                                label={`${snap.storageUsagePercent.toFixed(1)}%`}
                                style={{ minWidth: 110 }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
            </>
          )}

          {/* REPORTS TAB */}
          {detailTabId === "reports" && (
            <>
              {reportsError && <Alert variant="danger" className="mb-3">{reportsError}</Alert>}
              {reportsLoading && (
                <div className="text-center py-4">
                  <Spinner animation="border" className="me-2" />
                  <span className="text-muted">Loading report runs…</span>
                </div>
              )}
              {!reportsLoading && reports.length === 0 && (
                <div className="text-muted py-4 text-center">
                  <p>No report runs found.</p>
                </div>
              )}
              {!reportsLoading && reports.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-hover report-runs-table">
                    <thead>
                      <tr>
                        <th>Report</th>
                        <th>Status</th>
                        <th>Started</th>
                        <th>Completed</th>
                        <th>Generated</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((run) => (
                        <tr key={run.m365ReportRunId}>
                          <td className="report-title">{run.reportTitle}</td>
                          <td>
                            <span className={`badge report-status-badge ${
                              run.reportStatus === "Complete" ? "bg-success" :
                              run.reportStatus === "Failed" ? "bg-danger" :
                              "bg-info"
                            }`}>
                              {run.reportStatus}
                            </span>
                          </td>
                          <td className="report-date text-nowrap">{formatDateTime(run.reportStartDate)}</td>
                          <td className="report-date text-nowrap">{formatDateTime(run.reportEndDate)}</td>
                          <td className="text-center fw-semibold">{run.generatedReportsCount}</td>
                          <td>
                            {run.generatedReportsCount === 0 ? (
                              <span className="text-muted small">N/A</span>
                            ) : (
                              <Button
                                ref={(btn) => {
                                  if (btn) reportButtonRefs.current[run.m365ReportRunId] = btn;
                                }}
                                variant="link"
                                size="sm"
                                className="report-action-btn p-0 text-decoration-none"
                                disabled={generatedReportsLoading}
                                onClick={() => handleViewGeneratedReports(run.m365ReportRunId)}
                                title="View generated report(s)"
                              >
                                <FaFileDownload className="me-1" />
                                {expandedReportRunId === run.m365ReportRunId ? "Hide" : "View"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Reports Tooltip */}
          {expandedReportRunId && reportButtonRefs.current[expandedReportRunId] && generatedReports.length > 0 && (
            <Overlay
              show={true}
              target={reportButtonRefs.current[expandedReportRunId]}
              placement="left"
              containerPadding={50}
              offset={[0, 25]}
            >
              {(props) => (
                <div
                  {...props}
                  className="report-tooltip"
                  style={{
                    ...props.style,
                    background: "#f9f9f9",
                    border: "1px solid #d9d9d9",
                    padding: "8px 12px",
                    borderRadius: "5px",
                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                    zIndex: 9999,
                    width: 600,
                  }}
                >
                  {generatedReports.map((report, idx) => (
                    <div
                      key={report.m365AdminReportId}
                      style={{
                        borderBottom: idx < generatedReports.length - 1 ? "1px dashed #d9d9d9" : "none",
                        padding: "5px 2px",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        wordBreak: "break-word",
                      }}
                    >
                      <span
                        style={{
                          color: "#0c63e4",
                          textDecoration: "underline",
                          cursor: "pointer",
                          flex: 1,
                        }}
                        onClick={() => window.open(report.reportLink, "_blank")}
                        title="Click to download report"
                      >
                        {report.reportFilename}
                      </span>
                      <FaExternalLinkAlt
                        style={{ color: "#0c63e4", cursor: "pointer", flexShrink: 0, fontSize: "11px" }}
                        onClick={() => window.open(report.reportLink, "_blank")}
                        title="Open in new tab"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Overlay>
          )}

          {expandedReportRunId && reportButtonRefs.current[expandedReportRunId] && generatedReports.length === 0 && (
            <Overlay
              show={true}
              target={reportButtonRefs.current[expandedReportRunId]}
              placement="left"
              containerPadding={50}
              offset={[0, 25]}
            >
              {(props) => (
                <div
                  {...props}
                  className="report-tooltip"
                  style={{
                    ...props.style,
                    background: "#f9f9f9",
                    border: "1px solid #d9d9d9",
                    padding: "8px 12px",
                    borderRadius: "5px",
                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                    zIndex: 9999,
                  }}
                >
                  <div className="text-muted small">No reports were generated for this run.</div>
                </div>
              )}
            </Overlay>
          )}

          {/* USERS TAB */}
          {detailTabId === "users" && (
            <>
              {!site?.groupId && (
                <Alert variant="warning" className="mb-3">
                  This site is not associated with a Microsoft 365 group.
                </Alert>
              )}
              {site?.groupId && (
                <>
                  {membersError && <Alert variant="danger" className="mb-3">{membersError}</Alert>}
                  {membersLoading && (
                    <div className="text-center py-4">
                      <Spinner animation="border" className="me-2" />
                      <span className="text-muted">Loading group members…</span>
                    </div>
                  )}
                  {!membersLoading && members.length === 0 && (
                    <div className="text-muted py-4 text-center">
                      <p>No members found.</p>
                    </div>
                  )}
                  {!membersLoading && members.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover members-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member, idx) => (
                            <tr key={idx}>
                              <td className="member-name">{member.displayName}</td>
                              <td className="member-email">{member.email}</td>
                              <td>
                                <span className="badge member-role bg-light text-dark border">
                                  {member.role || "Member"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {site.groupId ? (
            <div hidden={detailTabId !== "files"}>
              <ResourceFileInventory key={`${site.guid}:${site.groupId}`} groupId={site.groupId} />
            </div>
          ) : detailTabId === "files" && (
            <Alert variant="info">
              This file inventory view requires a site associated with a Microsoft 365 group.
            </Alert>
          )}

          {detailTabId === "archive" && (
            <>
              {archiveError && <Alert variant="danger" className="mb-3">{archiveError}</Alert>}

              <Card className="mb-3">
                <Card.Header className="fw-semibold">Archive Administration</Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    Queue archive stages for this site. Processing runs asynchronously through report queues.
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={archiveActionLoading}
                      onClick={() => handleArchiveAction("backup")}
                    >
                      Start Backup (37)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-warning"
                      disabled={archiveActionLoading || archiveRuns.length === 0}
                      onClick={() => handleArchiveAction("readonly")}
                    >
                      Queue Readonly (38)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={archiveActionLoading || archiveRuns.length === 0}
                      onClick={() => handleArchiveAction("cleanup")}
                    >
                      Queue Cleanup (39)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={archiveLoading}
                      onClick={loadArchiveRuns}
                    >
                      Refresh
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="fw-semibold">Archive Runs</Card.Header>
                <Card.Body>
                  {archiveLoading && <div className="text-muted">Loading archive runs...</div>}
                  {!archiveLoading && archiveRuns.length === 0 && (
                    <div className="text-muted">No archive runs yet.</div>
                  )}
                  {!archiveLoading && archiveRuns.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Stage</th>
                            <th>Status</th>
                            <th>Requested By</th>
                            <th>Requested</th>
                            <th>Request Guid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {archiveRuns.map(run => (
                            <tr key={run.guid}>
                              <td className="text-nowrap text-capitalize">{run.stageName}</td>
                              <td>
                                <span className={`badge ${
                                  run.reportStatus === "Complete"
                                    ? "bg-success"
                                    : run.reportStatus === "Failed"
                                    ? "bg-danger"
                                    : "bg-info"
                                }`}>
                                  {run.reportStatus}
                                </span>
                              </td>
                              <td>{run.createUser}</td>
                              <td className="text-nowrap">{formatDateTime(run.createDt)}</td>
                              <td className="font-monospace small">{run.guid}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}

          <IncreaseStorageModal
            show={showIncreaseModal}
            onHide={() => setShowIncreaseModal(false)}
            siteGuid={site.guid}
            siteTitle={site.title}
            siteUrl={site.url}
            storageQuotaMb={site.storageQuotaMb}
            storageUsedMb={site.storageUsedMb}
            storageUsagePercent={site.storageUsagePercent}
            isAdminMode={true}
          />
        </>
      )}
    </>
  );

  return embedded ? (
    <div className="admin-group-detail-embedded">{content}</div>
  ) : (
    <Container className="mt-4" style={{ maxWidth: 960 }}>{content}</Container>
  );
};

export default AdminGroupDetail;
